import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { Form, FormFieldOptions, FormResponseValue, Product } from "@tellescope/types-models";
import { productIsVisible } from "./productIsVisible";
import { ProductWithStripePrice } from "./types";

export interface UseStripeProductsArgs {
  productIds: string[];
  enduserId: string;
  formResponses: FormResponseValue[];
  form: Form;
  options: FormFieldOptions;
}

/**
 * Loads the products a Stripe field may offer, joined with their live Stripe prices.
 *
 * `products` holds only what is actually offerable: visible per its `showCondition` and carrying a
 * price row. A product with no price cannot be charged for, so offering it would produce a checkout
 * that fails server-side.
 *
 * `isTerminal` reports that the selection step can go no further — it failed to load, or every
 * product was filtered out — so callers know to drop the "Continue to Payment" affordance rather
 * than leaving a control that can never be satisfied.
 */
export const useStripeProducts = ({ productIds, enduserId, formResponses, form, options }: UseStripeProductsArgs) => {
  const enduser = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
  });

  const stripeProductsQuery = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: {},
    select: (data) =>
      data?.pages.reduce<Product[]>((acc, curr) => {
        const matchingProducts = curr.filter((product) => {
          if (productIds.length && !productIds.includes(product.id)) {
            return false;
          }

          return productIsVisible(product.id, {
            options,
            formResponses,
            form,
            dateOfBirth: enduser?.data?.dateOfBirth,
            gender: enduser?.data?.gender,
            state: enduser?.data?.state,
          });
        });
        acc.push(...matchingProducts);
        return acc;
      }, []),
  });

  const stripeProductPricesQuery = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Stripe",
      type: "product-prices",
      id: stripeProductsQuery.data?.map(({ id }) => id).join(","),
      query: options.stripeKey,
    },
    enabled: !!stripeProductsQuery.data?.length,
    select: (data) => data.data as { products: ProductWithStripePrice[] },
  });

  const error = stripeProductsQuery.error ?? stripeProductPricesQuery.error;
  const isLoading = stripeProductsQuery.isLoading || stripeProductPricesQuery.isLoading;
  const prices = stripeProductPricesQuery.data?.products;
  const products = (stripeProductsQuery.data ?? []).filter(({ id }) => prices?.some(({ _id }) => _id === id));

  return {
    products,
    prices,
    error,
    isLoading,
    isTerminal: !!error || (!isLoading && !products.length),
  };
};
